import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import LoginModal from './auth/LoginModal';
import SignupModal from './auth/SignupModal';
import { useAuth } from '../contexts/AuthContext';

const ALL_TAGS = [
  // Vibe / crew
  "#找搭子", "#一起运动", "#周末组队", "#下班约练", "#临时缺人", "#新手友好", "#高手带飞",
  "#拼场AA", "#同频运动", "#一起变强", "#固定搭子", "#拼场成功", "#爆汗局", "#菜但爱玩",
  // Sports
  "#羽毛球", "#Pickleball", "#攀岩", "#徒步", "#黑道", "#野攀", "#刻画", "#平花", "#backcountry",
  "#Alllevels", "#Cardio", "#Running", "#Tennis", "#Basketball", "#Football", "#Fitness",
  "#lagree", "#HIIT", "#Yoga", "#Pilates", "#Skiing", "#Hiking", "#Cycling", "#Golf",
  // Moves / terms
  "#EasyRun", "#TempoRun", "#Smash", "#DropShot", "#Ace", "#ServeAndVolley", "#Topspin",
  "#BreakPoint", "#MatchPoint", "#Dink", "#KitchenLine", "#ThirdShotDrop", "#LegDay",
  "#Beta", "#Dyno", "#Sloper", "#Belay", "#PowderDay", "#TeeTime", "#Birdie", "#Putting",
];

const HERO_HEIGHT = 520;

interface TagData {
  id: string;
  text: string;
  x: number;
  y: number;
  duration: number;
  delay: number;
}

function shuffleArray<T>(array: T[]): T[] {
  const newArr = [...array];
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
  }
  return newArr;
}

const HeroSection: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [tags, setTags] = useState<TagData[]>([]);
  const [displayTags, setDisplayTags] = useState<string[]>([]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestRef = useRef<number>();

  useEffect(() => {
    const shuffled = shuffleArray(ALL_TAGS);
    setDisplayTags(shuffled.slice(0, 55));
  }, []);

  const calculateLayout = useCallback(() => {
    if (displayTags.length === 0) return;

    const count = displayTags.length;
    const width = window.innerWidth;
    const height = HERO_HEIGHT;
    const ratio = width / height;

    const rows = Math.ceil(Math.sqrt(count / ratio));
    const cols = Math.ceil(count / rows);
    const cellWidth = 100 / cols;
    const cellHeight = 100 / rows;

    const newTags: TagData[] = displayTags.map((text, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const jitterX = Math.random() * (cellWidth * 0.7);
      const jitterY = Math.random() * (cellHeight * 0.7);
      return {
        id: `hero-tag-${index}-${text}`,
        text,
        x: col * cellWidth + jitterX,
        y: row * cellHeight + jitterY,
        duration: 6 + Math.random() * 8,
        delay: Math.random() * -20,
      };
    });

    setTags(newTags);
  }, [displayTags]);

  useEffect(() => {
    calculateLayout();
    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateLayout, 200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateLayout]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const moveX = (e.clientX / window.innerWidth) * 14;
      const moveY = (e.clientY / HERO_HEIGHT) * 14;
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      requestRef.current = requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.style.transform = `translate(-${moveX}px, -${moveY}px)`;
        }
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  if (isAuthenticated) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: 'auto', md: `${HERO_HEIGHT}px` },
        minHeight: { xs: 480, md: HERO_HEIGHT },
        overflow: 'hidden',
        backgroundColor: '#faf9ff',
        fontFamily: '"Nunito Sans", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
        userSelect: 'none',
      }}
    >
      <style>{`
        @keyframes float {
          0%   { transform: translate(0, 0); }
          25%  { transform: translate(5px, 10px); }
          50%  { transform: translate(0, 20px); }
          75%  { transform: translate(-5px, 10px); }
          100% { transform: translate(0, 0); }
        }
        .shua-tag-animation {
          animation-name: float;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        .hashtag-hover-effect {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hashtag-hover-effect:hover {
          color: #533B7D;
          text-shadow: 0 0 20px rgba(83, 59, 125, 0.8), 0 0 40px rgba(83, 59, 125, 0.5);
          transform: scale(1.4) !important;
          opacity: 1;
          z-index: 50;
          transition: all 0.2s ease-in-out;
        }
      `}</style>

      {/* Bottom fade into events section */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '140px',
          background: 'linear-gradient(to bottom, transparent, #ffffff)',
          zIndex: 25,
          pointerEvents: 'none',
        }}
      />

      {/* Floating hashtag background */}
      <div
        ref={containerRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', willChange: 'transform' }}
      >
        {tags.map(tag => (
          <div
            key={tag.id}
            className="hashtag-hover-effect whitespace-nowrap shua-tag-animation"
            style={{
              position: 'absolute',
              left: `${tag.x}%`,
              top: `${tag.y}%`,
              animationDuration: `${tag.duration}s`,
              animationDelay: `${tag.delay}s`,
              color: 'rgba(83, 59, 125, 0.07)',
              fontSize: 'clamp(18px, 2vw, 26px)',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              pointerEvents: 'auto',
            }}
          >
            {tag.text}
          </div>
        ))}
      </div>

      {/* Hero content */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20,
          px: { xs: 3, sm: 4 },
          py: { xs: 6, md: 4 },
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        {/* Main headline */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: { xs: 1.5, md: 2 }, mb: 2.5 }}>
          <Box
            component="img"
            src="/assets/logo.svg"
            alt="Shua"
            sx={{ height: { xs: '3.2rem', sm: '4.4rem', md: '5.5rem' }, width: 'auto', display: 'block' }}
          />
          <Typography
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.8rem', sm: '3.8rem', md: '5rem' },
              lineHeight: 1.1,
              color: '#533B7D',
              fontFamily: 'inherit',
              letterSpacing: '-0.02em',
            }}
          >
            is play
          </Typography>
        </Box>

        {/* Subtitle */}
        <Typography
          sx={{
            fontSize: { xs: '1.05rem', sm: '1.15rem', md: '1.2rem' },
            color: '#444',
            maxWidth: 560,
            lineHeight: 1.85,
            mb: 4,
            fontFamily: 'inherit',
            fontWeight: 400,
          }}
        >
          Your friends are pulling ahead? <strong style={{ color: '#533B7D' }}>Close the gap.</strong><br />
          Got skills to share? <strong style={{ color: '#533B7D' }}>Lead a session, get paid</strong> — no cert required.<br />
          Just here to play? <strong style={{ color: '#533B7D' }}>Your crew is one tap away.</strong><br />
          <span style={{ color: '#888', fontSize: '0.95em' }}>All levels. Zero gatekeeping.</span>
        </Typography>

        {/* CTA */}
        <Button
          variant="contained"
          size="large"
          onClick={() => setSignupOpen(true)}
          sx={{
            pointerEvents: 'auto',
            backgroundColor: '#533B7D',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.05rem',
            px: 5,
            py: 1.5,
            borderRadius: '999px',
            boxShadow: '0 4px 16px rgba(83,59,125,0.3)',
            textTransform: 'none',
            fontFamily: 'inherit',
            '&:hover': {
              backgroundColor: '#422F64',
              boxShadow: '0 6px 20px rgba(83,59,125,0.4)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          Join Shua
        </Button>
      </Box>

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSwitchToSignup={() => { setLoginOpen(false); setSignupOpen(true); }}
      />
      <SignupModal
        open={signupOpen}
        onClose={() => setSignupOpen(false)}
        onSwitchToLogin={() => { setSignupOpen(false); setLoginOpen(true); }}
        onSignupSuccess={() => setSignupOpen(false)}
      />
    </Box>
  );
};

export default HeroSection;
